import { useEffect, useState } from 'react';
import { createFilterOptions } from '@mui/material';
import clsx from 'clsx';

import { Autocomplete, Chip, List } from '@atom/mui';
import colors from '@atom/styles/colors';
import { TaskField } from '@atom/types/task';
import {
  CustomFieldView,
  getTaskFieldWidth,
} from '@atom/utilities/taskUtilities';

import classes from './taskFields.module.css';

const { ListItemText } = List;

const filterOptions = createFilterOptions<string>({
  matchFrom: 'any',
  limit: 250,
});

interface Props {
  field: TaskField;
  onChange: (fieldId: string, value: any) => void;
  isDisabled?: boolean;
  error?: boolean;
  helperText?: string;
  onClick?: () => void;
  taskId?: string;
  customFieldView: CustomFieldView;
}

const TaskEnumMultipleField = ({
  field,
  onChange,
  isDisabled,
  error,
  helperText,
  onClick,
  customFieldView,
}: Props) => {
  const [values, setValues] = useState<string[]>([]);
  const [query, setQuery] = useState<string>('');

  const styles = {
    chip: {
      marginTop: '5px',
      marginRight: '5px',
      backgroundColor: colors.neutral.ash,
      borderRadius: '20px',
      opacity: isDisabled ? 0.5 : 1,
    },
  };

  const handleChange = (newValues: string[]) => {
    setValues(newValues);
    onChange(field.id, newValues);
  };

  const handleDeselect = (value: string) => {
    if (isDisabled) {
      return;
    }

    const newValues = values.filter(option => option !== value);
    setValues(newValues);
    onChange(field.id, newValues);
  };

  useEffect(() => {
    setValues(field?.value || []);
    setQuery('');
  }, [field?.value]);

  const title =
    field.required && customFieldView !== CustomFieldView.FILTER
      ? `* ${field.title}`
      : field.title;

  const containerWidth = {
    maxWidth: getTaskFieldWidth(customFieldView),
  };

  return (
    <div
      onClick={onClick}
      className={classes.fieldContainer}
      style={containerWidth}
    >
      <div
        className={clsx(classes.fieldTitle, error && classes.fieldTitleError)}
      >
        {title}
      </div>
      <Autocomplete
        id={field?.id}
        onClick={onClick}
        multiple
        disableClearable
        options={field?.enumeration}
        disabled={isDisabled}
        inputValue={query}
        error={error}
        filterOptions={filterOptions}
        errorHelperText={helperText}
        onInputChange={(event, newQuery) => setQuery(newQuery || '')}
        getOptionLabel={item => item}
        placeholder={!values.length ? 'Search' : ''}
        value={values}
        disableCloseOnSelect
        TextFieldProps={{ onClick }}
        onChange={(event, newValues) => {
          handleChange(newValues);
        }}
        renderOption={(optionProps, item) => (
          <li key={item} {...optionProps}>
            <ListItemText primary={item} />
          </li>
        )}
        renderValue={selected => {
          return selected.map(item => (
            <Chip
              key={item}
              style={styles.chip}
              label={item}
              onDelete={() => handleDeselect(item)}
            />
          ));
        }}
      />
    </div>
  );
};

export default TaskEnumMultipleField;
